import { useContext, useMemo, useState } from 'react'
import { useI18n } from '../../../provider/i18n'
import { MdLocationPin } from 'react-icons/md'
import { Blue300 } from '../../../lib/colors'
import { BsCalendarHeartFill } from 'react-icons/bs'
import moment from 'moment'
import { UseFormReturn } from 'react-hook-form'
import { BasketForm } from '../NewBasket'
import { CollectionPointSelector } from '../../../components/CollectionPointSelector'
import DateButton from '../../../components/DateButton'
import { BasketContext, isTestBasketItem } from '../../../provider/basket'
import { useAtomValue, useSetAtom } from 'jotai'
import { selectCollectionSlotModalDataAtom } from '../../../store'
import { availableLaboratoriesSubscriptionAtom } from '../../../store/subscription.atoms'

type StationaryCollectionFormProps = {
  form: UseFormReturn<BasketForm>
}

export const StationaryCollectionForm = ({
  form,
}: StationaryCollectionFormProps) => {
  const i18n = useI18n()
  const { items } = useContext(BasketContext)
  const {
    watch,
    setValue,
    formState: { errors },
  } = form

  const [{ data: availableLaboratoriesData }] = useAtomValue(
    availableLaboratoriesSubscriptionAtom
  )

  const testItems = useMemo(() => items.filter(isTestBasketItem), [items])

  const testIds = useMemo(
    () => testItems.map((item) => item.test.id),
    [testItems]
  )

  const uniqueTestIds = useMemo(
    () => testIds.filter((id, idx) => testIds.indexOf(id) === idx),
    [testIds]
  )

  const noCompatiblePoint = useMemo(() => {
    if (uniqueTestIds.length === 0 || !availableLaboratoriesData?.available_laboratory) return false
    return !availableLaboratoriesData.available_laboratory.some((lab) => {
      const labTestIds = new Set(lab.laboratoryTests.map((lt) => lt.testId))
      return uniqueTestIds.every((id) => labTestIds.has(id))
    })
  }, [availableLaboratoriesData, uniqueTestIds])

  const testCompatibility = useMemo(() => {
    if (!noCompatiblePoint || !availableLaboratoriesData?.available_laboratory) return []
    const labs = availableLaboratoriesData.available_laboratory
    return testItems
      .filter((item, idx, arr) => arr.findIndex((i) => i.test.id === item.test.id) === idx)
      .map((item) => {
        const supportingPoints = labs
          .filter((lab) =>
            lab.laboratoryTests.some((lt) => lt.testId === item.test.id)
          )
          .flatMap((lab) => lab.collectionPoints)
          .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))
        return { test: item.test, points: supportingPoints }
      })
  }, [noCompatiblePoint, availableLaboratoriesData, testItems])

  const collectionPointId = watch('collectionPointId')
  const collectionSlotId = watch('collectionSlotId')
  const collectionSlotDateFrom = watch('collectionSlotDateFrom')
  const isVoucher = watch('isVoucher')
  const [isVoucherEnabled, setIsVoucherEnabled] = useState(false)
  const setSelectCollectionSlotModalData = useSetAtom(
    selectCollectionSlotModalDataAtom
  )

  return (
    <>
      <div className="p-4 flex gap-4 items-center border-b border-b-border border-opacity-20">
        <div className="flex-col flex md:flex-row md:justify-between w-full gap-4">
          <div className="flex flex-row items-center md:justify-center text-left leading-tight text-sm text-blue-700">
            <MdLocationPin size={24} color={Blue300} />
            <div className="flex flex-col justify-center text-left leading-tight text-sm text-blue-700">
              <p className="font-bold ml-2">
                {i18n.t('screens.basket.form.collectionPoint.label')}
              </p>
            </div>
          </div>
          <div className="flex justify-center md:justify-end gap-4 w-full md:w-auto">
            <CollectionPointSelector
              testIds={testIds}
              value={collectionPointId}
              onChange={(pointId, point) => {
                setValue('collectionPointId', pointId)
                setValue('selectedCollectionPoint', {
                  address: point.address,
                  postalCode: point.postalCode,
                  city: point.city,
                  location: {
                    coordinates: (
                      point.location as {
                        type: string
                        coordinates: [number, number]
                      }
                    ).coordinates,
                  },
                })
                setValue('collectionSlotId', undefined)
                setValue('collectionSlotDateFrom', undefined)
                setValue('isVoucher', false)
                setIsVoucherEnabled(
                  !!(point as any).isVoucherEnabled
                )
              }}
              error={errors.collectionPointId?.message as string | undefined}
              showLabel={false}
              className="w-full md:w-auto"
            />
          </div>
        </div>
      </div>
      {noCompatiblePoint && (
        <div className="p-4 flex flex-col gap-3 border-b border-b-border border-opacity-20 bg-amber-50">
          <p className="text-sm font-bold text-amber-800">
            {i18n.t('screens.basket.sections.stationaryCollection.noCompatiblePoint.title')}
          </p>
          <div className="flex flex-col gap-2">
            {testCompatibility.map(({ test, points }) => (
              <div key={test.id}>
                <p className="text-xs font-bold text-blue-700">
                  {test.shortName || test.name}
                </p>
                {points.length > 0 ? (
                  <ul className="flex flex-col gap-0.5 mt-0.5">
                    {points.map((point) => (
                      <li key={point.id} className="text-xs text-blue-700">
                        {point.name} — {point.address}, {point.postalCode} {point.city}
                        {point.distance != null
                          ? ` • ${(point.distance / 1000).toFixed(1)} km`
                          : ''}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-red-700 mt-0.5">
                    {i18n.t('screens.basket.sections.stationaryCollection.noCompatiblePoint.noPointsForTest')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {collectionPointId && (
        <>
          {isVoucherEnabled && (
            <div className="p-4 flex gap-4 items-center border-b border-b-border border-opacity-20">
              <div className="flex flex-col gap-4 w-full">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!isVoucher}
                      onChange={() => {
                        setValue('isVoucher', false)
                      }}
                    />
                    <span className="text-sm text-blue-700 font-bold">
                      Termin
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!!isVoucher}
                      onChange={() => {
                        setValue('isVoucher', true)
                        setValue('collectionSlotId', undefined)
                        setValue('collectionSlotDateFrom', undefined)
                      }}
                    />
                    <span className="text-sm text-blue-700 font-bold">
                      Voucher
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}
          {isVoucher ? (
            <div className="p-4 flex gap-4 items-center border-b border-b-border border-opacity-20">
              <div className="flex flex-col justify-center text-left leading-tight text-sm text-blue-700">
                <p>
                  Zamówienie ważne 90 dni od zakupu. Udaj się do punktu pobrań w
                  dowolnym momencie.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="p-4 flex gap-4 items-center border-b border-b-border border-opacity-20">
                <div className="flex-col flex md:flex-row md:justify-between w-full gap-4">
                  <div className="flex flex-row items-center md:justify-center text-left leading-tight text-sm text-blue-700">
                    <BsCalendarHeartFill size={24} color={Blue300} />
                    <div className="flex flex-col justify-center text-left leading-tight text-sm text-blue-700">
                      <p className="font-bold ml-2">
                        {i18n.t('screens.basket.form.collectionSlot.label')}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-center md:justify-end gap-4 w-full md:w-auto">
                    <DateButton
                      value={collectionSlotDateFrom}
                      isError={!!errors.collectionSlotId}
                      onClick={() => {
                        setSelectCollectionSlotModalData({
                          isOpen: true,
                          collectionPointId,
                          minDate: moment().toISOString(),
                          slotId: collectionSlotId,
                          onSelect: (slot) => {
                            setValue('collectionSlotId', slot.id)
                            setValue('collectionSlotDateFrom', slot.dateFrom)
                          },
                        })
                      }}
                    />
                    {errors.collectionSlotId && (
                      <p className="text-red-700 text-xs">
                        {errors.collectionSlotId.message as string}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {errors.collectionSlotId && (
                <div className="flex flex-col gap-2 md:hidden px-4 pb-4 border-b border-b-border border-opacity-20">
                  <p className="text-red-700 text-xs">
                    {errors.collectionSlotId.message as string}
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}
